import { useEffect, useRef, useState } from 'react';
import { useAuthStore, useProjectStore, useUIStore, useIssueStore } from '@/stores';
import { Navbar, Sidebar, GanttChart, VehicleNodes, VehicleNodesRoadmap, AuthModal, ForgotPasswordModal, ProjectModal, RiskPanel, ToDoList, VersionModal, ProjectListModal, TaskEditModal, NewProjectWizard, ComponentTemplateSelectModal, IssuesPage } from '@/components/features';
import type { GanttChartRef } from '@/components/features/GanttChart';
import type { Task } from '@/types';
import { Card, CardContent, Button, Input, Toast, Dock, Loader } from '@/components/ui';
import { Download, Save, Trash2, Sparkles, AlertTriangle, Check, AlertCircle, ChevronDown, Clock, FileSpreadsheet, Search } from 'lucide-react';
import { calculateTaskDates, extractFunctionalNodeMapping, extractFunctionalTaskMappingFromTasks, replaceFunctionalNodesInTemplate, extractCustomVehicleNodes } from '@/data/templates';
import { analyzeRisks, functionalTaskDescriptions, type FunctionalMapping } from '@/data/riskRules';
import { taskMatchingApi } from '@/api/client';
import { autoCalculateAllTaskStatuses } from '@/utils/taskStatus';
import type { ComponentTemplate } from '@/components/features/ComponentTemplateSelectModal';
import * as XLSX from 'xlsx';

function parseVersion(version: string | undefined): { major: number; minor: number } {
  if (!version) return { major: 1, minor: 0 };
  const parts = version.split('.');
  return {
    major: parseInt(parts[0]) || 1,
    minor: parseInt(parts[1]) || 0
  };
}

function formatVersion(major: number, minor: number): string {
  return `${major}.${minor}`;
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(date.getDate()).padStart(2, '0');
  const month = months[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  return `${day}${month}${year}`;
}

function exportRevisionHistoryToExcel(versions: any[], projectName: string) {
  const data = versions.map(v => ({
    'Version': `v${v.version}`,
    'Revision History': v.revision_history || '',
    'Change Description': v.change_description || '',
    'Date': formatDateShort(v.created_at)
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Revision History');

  const colWidths = [
    { wch: 10 },
    { wch: 40 },
    { wch: 40 },
    { wch: 15 }
  ];
  ws['!cols'] = colWidths;

  const fileName = `${projectName || 'Project'}_Revision_History_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

export function Dashboard() {
  const { isAuthenticated, verifySession, sessionRemainingTime, updateSessionRemainingTime } = useAuthStore();
  const { currentProject, tasks, loadProjects, createProject, updateProject, deleteProject, setTasks, isLoading, versions, loadVersions, setCurrentVersion, projects, setCurrentProject, currentVersion, deleteVersion, createVersion, isProjectSwitching } = useProjectStore();
  const { isSaveModalOpen, openSaveModal, closeSaveModal, showToast } = useUIStore();
  const { activeView, setActiveView } = useIssueStore();
  const ganttChartRef = useRef<GanttChartRef>(null);
  const ganttContainerRef = useRef<HTMLDivElement>(null);
  
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [risks, setRisks] = useState<any[]>([]);
  const [isAnalyzingRisks, setIsAnalyzingRisks] = useState(false);
  const [riskAnalysisComplete, setRiskAnalysisComplete] = useState(false);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [editVersion, setEditVersion] = useState({ major: 1, minor: 0 });
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [versionHistory, setVersionHistory] = useState<{version: string; date: string; task_list?: Task[]; vehicle_nodes?: any}[]>([]);
  const [revisionHistory, setRevisionHistory] = useState('');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [isTaskEditModalOpen, setIsTaskEditModalOpen] = useState(false);
  const [showComponentTemplateModal, setShowComponentTemplateModal] = useState(false);
  const [pendingComponentTemplate, setPendingComponentTemplate] = useState<ComponentTemplate | null>(null);
  const [showTimingConfirm, setShowTimingConfirm] = useState(false);
  const [searchTask, setSearchTask] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Reset isProjectSwitching after a delay (for both project and version switching)
  useEffect(() => {
    if (isProjectSwitching) {
      const timer = setTimeout(() => {
        useProjectStore.setState({ isProjectSwitching: false });
      }, 500); // 500ms delay to ensure loader is visible
      return () => clearTimeout(timer);
    }
  }, [isProjectSwitching]);

  const handleSearchTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (ganttChartRef.current && searchTask.trim()) {
      ganttChartRef.current.scrollToTask(searchTask.trim());
    }
  };
  
  const originalDataRef = useRef({
    tasks: '',
    nodes: '',
  });
  
  const isInitializedRef = useRef(false);

  const handleExportChart = () => {
    if (ganttChartRef.current && currentProject) {
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
      const filename = `${currentProject.name}_甘特图_${dateStr}.png`;
      ganttChartRef.current.exportToPNG(filename);
    }
  };

  const handleTaskClick = (task: Task) => {
    setEditingTask(task);
    setIsTaskEditModalOpen(true);
  };

  const handleTaskSave = async (updatedTask: Task) => {
    const updatedTasks = tasks.map(t => t.id === updatedTask.id ? updatedTask : t);
    setTasks(updatedTasks);
    showToast('Task updated', 'success');
  };

  useEffect(() => {
    const handleProjectChange = (e: CustomEvent) => {
      const { taskList, nodes } = e.detail;
      originalDataRef.current = {
        tasks: JSON.stringify(taskList || []),
        nodes: JSON.stringify(nodes || {}),
      };
      isInitializedRef.current = true;
      setSaved(false);
    };
    
    // 监听节点变更事件（不重置 originalDataRef，这样 hasNodesChanged 会返回 true）
    const handleNodesChange = (e: CustomEvent) => {
      // 只需要标记为未保存，不需要更新 originalDataRef
      setSaved(false);
    };
    
    window.addEventListener('projectChanged', handleProjectChange as EventListener);
    window.addEventListener('nodesChanged', handleNodesChange as EventListener);
    return () => {
      window.removeEventListener('projectChanged', handleProjectChange as EventListener);
      window.removeEventListener('nodesChanged', handleNodesChange as EventListener);
    };
  }, []);

  useEffect(() => {
    if (currentProject && !isInitializedRef.current) {
      originalDataRef.current = {
        tasks: JSON.stringify(currentProject.taskList || []),
        nodes: JSON.stringify(currentProject.nodes || {}),
      };
      isInitializedRef.current = true;
    }
  }, [currentProject]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.version-dropdown-container')) {
        setShowVersionDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const hasNodesChanged = currentProject && 
    isInitializedRef.current &&
    originalDataRef.current.nodes !== '' &&
    JSON.stringify(currentProject.nodes) !== originalDataRef.current.nodes;

  const hasTasksChanged = currentProject && 
    tasks.length > 0 && 
    isInitializedRef.current &&
    originalDataRef.current.tasks !== '' &&
    JSON.stringify(tasks) !== originalDataRef.current.tasks;

  // 检测任务时间变化（用于版本升级）- 只检测时间字段
  const hasTaskTimeChanged = currentProject && 
    tasks.length > 0 && 
    isInitializedRef.current &&
    originalDataRef.current.tasks !== '' &&
    (() => {
      const originalTasks = JSON.parse(originalDataRef.current.tasks);
      const originalMap = new Map(originalTasks.map((t: any) => [t.id, t]));
      return tasks.some(task => {
        const original = originalMap.get(task.id);
        if (!original) return false;
        return original.start !== task.start || original.end !== task.end;
      });
    })();

  const hasChanges = hasNodesChanged || hasTasksChanged;

  const generateNodesChangeDetails = (): string[] => {
    if (!hasNodesChanged || !currentProject) return [];
    
    const changes: string[] = [];
    const originalNodes = JSON.parse(originalDataRef.current.nodes);
    const currentNodes = currentProject.nodes;
    
    const nodeLabels: Record<string, string> = {
      SOR: 'SOR', SF: 'SF', VDR: 'VDR', VTC: 'VTC', VDC: 'VDC',
      MC1: 'MC1', MC2: 'MC2', APPV: 'APPV', PPV: 'PPV', S: 'S', SOP: 'SOP', SORP: 'SORP'
    };
    
    Object.keys(nodeLabels).forEach(key => {
      const original = originalNodes[key];
      const current = currentNodes[key];
      if (original !== current) {
        if (original && current) {
          changes.push(`${nodeLabels[key]} ${original} -> ${current}`);
        } else if (current) {
          changes.push(`${nodeLabels[key]} 设置为 ${current}`);
        } else {
          changes.push(`${nodeLabels[key]} 已删除`);
        }
      }
    });
    
    return changes;
  };

  const generateTasksChangeDetails = (): string[] => {
    if (!hasTasksChanged) return [];

    const changes: string[] = [];
    const originalTasks = JSON.parse(originalDataRef.current.tasks);
    const currentTasks = tasks;

    const originalMap = new Map(originalTasks.map((t: any) => [t.id, t]));

    // 只记录任务时间变化（开始时间和结束时间），不记录状态变化
    currentTasks.forEach(task => {
      const original = originalMap.get(task.id);
      if (original) {
        if (original.start !== task.start) {
          changes.push(`${task.name} 开始时间 ${original.start} -> ${task.start}`);
        }
        if (original.end !== task.end) {
          changes.push(`${task.name} 结束时间 ${original.end} -> ${task.end}`);
        }
        // 注意：状态变化不记录到变更履历中
      }
    });

    return changes;
  };

  const nodesChangeDetails = generateNodesChangeDetails();
  const tasksChangeDetails = generateTasksChangeDetails();

  useEffect(() => {
    if (isSaveModalOpen && currentProject) {
      const { major, minor } = parseVersion(currentProject.version);
      
      let newMajor = major;
      let newMinor = minor;
      
      // 只有节点变化或任务时间变化时才升级版本号
      // 任务状态变化不升级版本号
      if (hasNodesChanged) {
        newMajor = major + 1;
        newMinor = 0;
      } else if (hasTaskTimeChanged) {
        newMinor = minor + 1;
      }
      
      setEditVersion({ major: newMajor, minor: newMinor });
      setVersionName(formatVersion(newMajor, newMinor));
    }
  }, [isSaveModalOpen, currentProject, hasNodesChanged, hasTaskTimeChanged]);

  const handleSave = () => {
    if (!currentProject || !hasChanges) return;
    openSaveModal();
  };

  const handleConfirmSave = async () => {
    if (!currentProject) return;
    
    // 检测是否有任何变更（包括状态变化）
    const hasAnyChanges = hasNodesChanged || hasTasksChanged;
    
    // 检测是否需要升级版本号（只有节点变化或任务时间变化才升级版本）
    const shouldUpgradeVersion = hasNodesChanged || hasTaskTimeChanged;
    
    // 检测是否是新建项目（ID 以 temp_ 开头）
    const isNewProject = currentProject.id.startsWith('temp_');
    
    // 如果不是新建项目且没有变更，不保存
    if (!isNewProject && !hasAnyChanges) {
      showToast('没有检测到变更', 'info');
      closeSaveModal();
      return;
    }
    
    setIsSaving(true);
    try {
      // 只有需要升级版本时才记录变更履历
      const revisionHistoryText = shouldUpgradeVersion || isNewProject
        ? [...nodesChangeDetails, ...tasksChangeDetails].join('; ')
        : '';
      
      // 版本号：需要升级时用新版本，否则保持当前版本
      const versionToSave = shouldUpgradeVersion || isNewProject ? versionName : currentProject.version;
      const majorToSave = shouldUpgradeVersion || isNewProject ? editVersion.major : currentProject.majorVersion;
      const minorToSave = shouldUpgradeVersion || isNewProject ? editVersion.minor : currentProject.minorVersion;
      
      let savedProjectId = currentProject.id;
      
      if (isNewProject) {
        // 新建项目：调用 createProject
        const newProject = await createProject({
          name: currentProject.name,
          description: currentProject.description,
          supplier: currentProject.supplier,
          nodes: currentProject.nodes,
          taskList: tasks,
          version: versionToSave,
          status: 'active',
          vehicleMilestoneTemplate: currentProject.vehicleMilestoneTemplate,
        });
        savedProjectId = newProject.id;
      } else {
        // 更新现有项目
        // updateProject 会自动创建版本历史记录（见 server.cjs 第 318-330 行）
        console.log('Update project:', {
          id: currentProject.id,
          nodes: currentProject.nodes,
          taskList: tasks.length,
          version: versionToSave,
          majorVersion: majorToSave,
          minorVersion: minorToSave,
          revisionHistory: revisionHistoryText,
        });
        await updateProject(currentProject.id, {
          nodes: currentProject.nodes,
          taskList: tasks,
          version: versionToSave,
          majorVersion: majorToSave,
          minorVersion: minorToSave,
          revisionHistory: revisionHistoryText,
          changeDescription: shouldUpgradeVersion ? revisionHistory : '',
          vehicleMilestoneTemplate: currentProject.vehicleMilestoneTemplate,
        });
      }
      
      originalDataRef.current = {
        tasks: JSON.stringify(tasks),
        nodes: JSON.stringify(currentProject.nodes),
      };
      
      setSaved(true);
      setRevisionHistory('');
      closeSaveModal();
      
      if (isNewProject) {
        showToast('项目创建成功', 'success');
      } else if (shouldUpgradeVersion) {
        showToast('项目保存成功', 'success');
      } else {
        showToast('项目已保存（状态更新，版本不变）', 'success');
      }
      
      // 始终重新加载版本列表，确保当前版本信息是最新的
      // 即使只是状态变更，也需要更新版本列表中的当前版本记录
      await loadVersions(savedProjectId);
      
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Save error:', error);
      showToast('项目保存失败，请重试', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!currentProject || !currentVersion) return;
    try {
      await deleteVersion(currentProject.id, currentVersion.version, currentVersion.id);
      setCurrentVersion(null);
      setShowDeleteConfirm(false);
      showToast('Version deleted successfully', 'success');
      await loadVersions(currentProject.id);
    } catch (error) {
      console.error('Delete error:', error);
      showToast('Failed to delete version', 'error');
    }
  };

  const handleRiskAnalysis = async () => {
    setShowRiskModal(true);
    setIsAnalyzingRisks(true);
    setRisks([]);
    setRiskAnalysisComplete(false);

    try {
      const functionalNodeMapping = extractFunctionalNodeMapping(currentProject?.vehicleMilestoneTemplate);
      const functionalTaskMapping = extractFunctionalTaskMappingFromTasks(tasks);

      const functionalMapping: FunctionalMapping = {
        nodes: functionalNodeMapping,
        tasks: functionalTaskMapping,
      };

      const analyzedRisks = await analyzeRisks(
        autoCalculateAllTaskStatuses(tasks),
        currentProject?.nodes || { VDR: '', APPV: '', S: '', SORP: '' },
        functionalMapping,
        async (functionalTask) => {
          try {
            const result = await taskMatchingApi.matchFunctionalTask({
              functionalTaskType: functionalTask,
              functionalTaskDescription: functionalTaskDescriptions[functionalTask] || '',
              projectTasks: tasks.map(t => ({
                name: t.name,
                description: t.description || '',
                start: t.start,
                end: t.end,
                phase: t.phase || '',
              })),
              projectContext: {
                name: currentProject?.name || '',
                oem: currentProject?.supplier || '',
              },
            });
            return result.matchedTaskName;
          } catch (error) {
            console.error('Task matching failed:', error);
            return null;
          }
        }
      );

      setRisks(analyzedRisks);
      setIsAnalyzingRisks(false);
      setTimeout(() => setRiskAnalysisComplete(true), 300);
    } catch (error) {
      console.error('Risk analysis failed:', error);
      showToast('风险分析失败', 'error');
      setIsAnalyzingRisks(false);
    }
  };

  const handleGenerateTiming = async () => {
    if (!currentProject?.nodes || Object.keys(currentProject.nodes).length === 0) {
      alert('Please set vehicle node dates first');
      return;
    }

    setShowComponentTemplateModal(true);
  };

  const handleComponentTemplateSelect = (template: ComponentTemplate) => {
    setShowComponentTemplateModal(false);
    
    if (tasks && tasks.length > 0) {
      setPendingComponentTemplate(template);
      setShowTimingConfirm(true);
    } else {
      generateTimingWithTemplate(template);
    }
  };

  const generateTimingWithTemplate = async (componentTemplate: ComponentTemplate) => {
    setIsSaving(true);
    try {
      const existingTaskStatus = new Map<string, string>();
      tasks.forEach(task => {
        existingTaskStatus.set(task.name, task.status);
      });

      const vehicleTemplate = currentProject?.vehicleMilestoneTemplate;
      const functionalNodeMapping = extractFunctionalNodeMapping(vehicleTemplate);
      const customVehicleNodes = extractCustomVehicleNodes(vehicleTemplate);
      
      const processedTemplate = replaceFunctionalNodesInTemplate(
        componentTemplate.phases,
        functionalNodeMapping
      );

      const generatedTasks = calculateTaskDates(
        processedTemplate,
        currentProject.nodes,
        functionalNodeMapping,
        false,
        customVehicleNodes
      );
      
      const tasksWithId = generatedTasks.map((task, index) => ({
        ...task,
        id: `task_${Date.now()}_${index}`,
        owner: '',
        status: (existingTaskStatus.get(task.name) || 'not-started') as const,
      }));

      setTasks(tasksWithId);
    } catch (error) {
      console.error('Generate timing error:', error);
    } finally {
      setIsSaving(false);
      setPendingComponentTemplate(null);
    }
  };

  const handleConfirmTimingOverwrite = () => {
    setShowTimingConfirm(false);
    if (pendingComponentTemplate) {
      generateTimingWithTemplate(pendingComponentTemplate);
    }
  };

  useEffect(() => {
    verifySession();
  }, []);

  // 检查 session 剩余时间并显示警告
  useEffect(() => {
    if (!isAuthenticated) {
      setShowSessionWarning(false);
      return;
    }

    // 更新剩余时间
    updateSessionRemainingTime();

    // 每分钟检查一次
    const interval = setInterval(() => {
      updateSessionRemainingTime();
      const authStore = useAuthStore.getState();
      const remaining = authStore.sessionRemainingTime;
      const SESSION_EXPIRY_WARNING_MS = 60 * 60 * 1000; // 1小时
      
      if (remaining > 0 && remaining < SESSION_EXPIRY_WARNING_MS) {
        setShowSessionWarning(true);
      } else {
        setShowSessionWarning(false);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadProjects();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // 当 currentProject 设置后，加载其版本历史
    if (currentProject?.id) {
      loadVersions(currentProject.id);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    if (projects.length > 0 && !currentProject) {
      setCurrentProject(projects[0]);
    }
  }, [projects, currentProject]);

  if (!isAuthenticated) {
    return (
      <>
        <AuthModal />
        <ForgotPasswordModal />
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <Toast />
      
      {/* Session Expiry Warning */}
      {showSessionWarning && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 flex items-center justify-center">
          <span className="text-yellow-800 text-sm">
            ⚠️ 登录即将过期，请及时保存工作内容。如需继续使用，请重新登录。
          </span>
          <button 
            onClick={() => {
              useAuthStore.getState().logout();
            }}
            className="ml-4 px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
          >
            重新登录
          </button>
        </div>
      )}
      
      <Navbar />
      
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        
        <main className="flex-1 overflow-auto p-6 relative">
          {/* Loading Overlay for Project Switching or Exporting */}
          {(isProjectSwitching || isExporting) && (
            <div className="absolute inset-0 bg-white z-50 flex items-center justify-center">
              <Loader size="lg" />
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-24 h-24 flex items-center justify-center">
                <Loader size="lg" />
              </div>
            </div>
          ) : currentProject ? (
            activeView === 'issues' ? (
              <IssuesPage />
            ) : (
            <div className="space-y-6">
              {/* Project Header with Action Buttons */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 relative">
                      <h2 className="text-2xl font-bold text-gray-800">{currentProject.name}</h2>
                      <button 
                        className="version-dropdown-container flex items-center gap-0.5 text-gray-500 hover:text-gray-700 transition-colors"
                        onClick={async (e) => {
                          e.stopPropagation();
                          setShowVersionDropdown(!showVersionDropdown);
                          if (!showVersionDropdown && currentProject) {
                            await loadVersions(currentProject.id);
                          }
                        }}
                      >
                        <span className="text-xs font-bold">v</span>
                        <span className="text-xs">{currentVersion?.version || currentProject.version || '1.0'}</span>
                        <ChevronDown className={`w-3 h-3 transition-transform ${showVersionDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Version History Dropdown */}
                      {showVersionDropdown && (
                        <div className="absolute top-full left-0 mt-1 z-50 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[500px]">
                          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <h3 className="font-bold text-gray-800">Project Revision Management</h3>
                            {versions.length > 0 && (
                              <button
                                onClick={() => exportRevisionHistoryToExcel(versions, currentProject?.name)}
                                className="flex items-center px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              >
                                <FileSpreadsheet className="w-3 h-3 mr-1" />
                                Export Excel
                              </button>
                            )}
                          </div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="text-left px-4 py-3 font-bold text-gray-600">Version</th>
                                <th className="text-left px-4 py-3 font-bold text-gray-600">Revision History</th>
                                <th className="text-left px-4 py-3 font-bold text-gray-600">Change Description</th>
                                <th className="text-left px-4 py-3 font-bold text-gray-600">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {versions.map((v) => (
                                <tr
                                  key={v.id}
                                  className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer"
                                  onClick={() => {
                                    // 显示 loading 遮罩
                                    useProjectStore.setState({ isProjectSwitching: true });

                                    // 使用服务器返回的正确字段名
                                    const tasksData = v.task_list || [];
                                    const nodesData = v.vehicle_nodes || {};

                                    setTasks(tasksData);
                                    if (currentProject) {
                                      useProjectStore.getState().updateVehicleNodes(nodesData);
                                    }
                                    setCurrentVersion(v);
                                    setShowVersionDropdown(false);
                                    originalDataRef.current = {
                                      tasks: JSON.stringify(tasksData),
                                      nodes: JSON.stringify(nodesData),
                                    };
                                    showToast(`已切换到版本 v${v.version}`, 'success');
                                  }}
                                >
                                  <td className="px-4 py-3 font-medium text-gray-700">v{v.version}</td>
                                  <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={v.revision_history}>{v.revision_history || '-'}</td>
                                  <td className="px-4 py-3 text-gray-600 max-w-[150px] truncate" title={v.change_description}>{v.change_description || '-'}</td>
                                  <td className="px-4 py-3 text-gray-500">{formatDateShort(v.created_at)}</td>
                                </tr>
                              ))}
                              {versions.length === 0 && (
                                <tr>
                                  <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                                    暂无历史版本
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-gray-600">{currentProject.description}</span>
                      <span className="text-gray-400">|</span>
                      <span className="text-gray-600">{currentProject.supplier}</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons - Dock Style */}
                  <Dock
                    items={[
                      {
                        icon: <Sparkles className="w-5 h-5" />,
                        label: 'Generate Timing',
                        onClick: isSaving ? undefined : handleGenerateTiming,
                        className: isSaving ? 'opacity-50 cursor-wait' : '',
                      },
                      {
                        icon: <AlertTriangle className="w-5 h-5" />,
                        label: 'Risk Analysis',
                        onClick: isAnalyzingRisks ? undefined : handleRiskAnalysis,
                        className: isAnalyzingRisks ? 'opacity-50 cursor-wait' : '',
                      },
                      {
                        icon: saved ? <Check className="w-5 h-5" /> : <Save className="w-5 h-5" />,
                        label: saved ? 'Saved' : hasChanges ? 'Save' : 'No Changes',
                        onClick: () => {
                          if (saved) {
                            showToast('Project saved', 'success');
                          } else if (hasChanges) {
                            handleSave();
                          } else {
                            showToast('No changes to save', 'error');
                          }
                        },
                        className: saved 
                          ? 'bg-green-100 border-green-300' 
                          : hasChanges 
                            ? 'hover:bg-green-50 border-green-200' 
                            : 'opacity-50',
                      },
                      {
                        icon: <Trash2 className="w-5 h-5" />,
                        label: 'Delete Version',
                        onClick: handleDelete,
                        className: 'hover:bg-red-50 hover:border-red-200',
                      },
                    ]}
                    panelHeight={60}
                    baseItemSize={40}
                    magnification={56}
                    distance={150}
                  />
                </div>
              </div>

              {/* Top Row: Risk Panel and Vehicle Nodes Roadmap */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ToDoList ganttChartRef={ganttChartRef} ganttContainerRef={ganttContainerRef} />
                <VehicleNodesRoadmap />
              </div>

              {/* Gantt Chart */}
              <Card>
                <div ref={ganttContainerRef}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-gray-500" />
                        {currentProject.name} Project Timing
                      </h3>
                      <button
                        onClick={handleExportChart}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="导出PNG"
                      >
                        <Download className="w-4 h-4 text-gray-600" />
                      </button>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center" style={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
                        <input
                          placeholder="Search..."
                          value={searchTask}
                          onChange={(e) => setSearchTask(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && ganttChartRef.current && searchTask.trim()) {
                              ganttChartRef.current.scrollToTask(searchTask.trim());
                            }
                          }}
                          style={{
                            fontFamily: 'inherit',
                            fontSize: 'inherit',
                            border: '1px solid #767676',
                            color: '#767676',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            marginRight: '-2rem',
                            height: '30px',
                            width: '15rem',
                            outline: 'none'
                          }}
                        />
                        <button
                          onClick={() => {
                            if (ganttChartRef.current && searchTask.trim()) {
                              ganttChartRef.current.scrollToTask(searchTask.trim());
                            }
                          }}
                          style={{
                            border: 'none',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px'
                          }}
                        >
                          <Search className="w-4 h-4" style={{ color: '#767676' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <GanttChart 
                    ref={ganttChartRef}
                    tasks={autoCalculateAllTaskStatuses(tasks)} 
                    vehicleNodes={currentProject.nodes}
                    height={400}
                    onTaskClick={handleTaskClick}
                    setIsExporting={setIsExporting}
                  />
                </CardContent>
                </div>
              </Card>

            </div>
            )
          ) : (
            <div className="flex items-center justify-center h-full">
              <Card className="p-12 text-center">
                <CardContent>
                  <h3 className="text-xl font-bold text-gray-700 mb-2">欢迎使用 Project Timing</h3>
                  <p className="text-gray-500 mb-6">请从左侧选择一个项目或创建新项目</p>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
      
      {/* Modals */}
      <NewProjectWizard />
      <VersionModal />
      <ProjectListModal />
      <TaskEditModal 
        isOpen={isTaskEditModalOpen}
        onClose={() => setIsTaskEditModalOpen(false)}
        task={editingTask}
        onSave={handleTaskSave}
      />
      
      {/* Save Confirm Modal */}
      {isSaveModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center text-blue-600 mb-4">
              <Save className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-bold">Save Project</h3>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-2">
                Project: <span className="font-medium text-gray-800">{currentProject?.name}</span>
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-700 flex-1 min-w-0">
                    <p>Changes detected:</p>
                    <ul className="list-disc list-inside mt-1 max-h-28 overflow-y-auto pr-2">
                      {[...nodesChangeDetails, ...tasksChangeDetails].map((change, idx) => (
                        <li key={idx}>{change}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{currentProject?.name} Version</span>
                  <input
                    type="text"
                    value={versionName}
                    onChange={(e) => {
                      const val = e.target.value;
                      setVersionName(val);
                      const parts = val.split('.');
                      if (parts.length === 2) {
                        setEditVersion({
                          major: parseInt(parts[0]) || 1,
                          minor: parseInt(parts[1]) || 0
                        });
                      }
                    }}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                    placeholder="1.0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Current: {currentProject?.version || '1.0'} → New: {versionName}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Change Description
                </label>
                <textarea
                  value={revisionHistory}
                  onChange={(e) => setRevisionHistory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={2}
                  placeholder="Enter change description..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={closeSaveModal}>
                Cancel
              </Button>
              <Button onClick={handleConfirmSave} isLoading={isSaving}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-red-600 mb-4">Delete Version</h3>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete this version?
            </p>
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-sm">
                <span className="text-gray-500">Project:</span>
                <span className="font-medium text-gray-800 ml-2">{currentProject?.name}</span>
              </div>
              <div className="text-sm mt-1">
                <span className="text-gray-500">Version:</span>
                <span className="font-medium text-gray-800 ml-2">{currentVersion?.version}</span>
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              This action cannot be undone. The project will remain, but this version will be permanently deleted.
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button variant="danger" onClick={confirmDelete}>Delete Version</Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Risk Analysis Modal */}
      {showRiskModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Risk Analysis Report</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowRiskModal(false)}>Close</Button>
            </div>
            
            {isAnalyzingRisks && !riskAnalysisComplete ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader />
              </div>
            ) : risks.length > 0 ? (
              <div className="space-y-3 animate-fadeIn">
                {risks.map((risk, index) => (
                  <div
                    key={risk.id}
                    className={`p-4 rounded-lg border border-gray-300 transition-all duration-300 ${riskAnalysisComplete ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="font-medium text-gray-800">{risk.name}</div>
                    <div className="text-sm mt-1 text-gray-600">
                      "{risk.taskAName}" ({risk.taskADate ? `completed: ${risk.taskADate}` : 'N/A'}) 
                      {risk.nodeBName && risk.nodeBDate && (
                        <> is later than "{risk.nodeBName}" ({risk.nodeBDate})</>
                      )}
                    </div>
                    <div className={`text-xs mt-2 font-medium ${
                      risk.severity === 'high'
                        ? 'text-red-600'
                        : risk.severity === 'medium'
                        ? 'text-orange-600'
                        : 'text-yellow-600'
                    }`}>
                      {risk.severity === 'high' ? 'High' : risk.severity === 'medium' ? 'Medium' : 'Low'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 animate-fadeIn">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>No risks found. Project is progressing well!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Component Template Select Modal */}
      <ComponentTemplateSelectModal
        isOpen={showComponentTemplateModal}
        onClose={() => setShowComponentTemplateModal(false)}
        onSelect={handleComponentTemplateSelect}
      />

      {/* Timing Overwrite Confirm Modal */}
      {showTimingConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center text-yellow-600 mb-4">
              <AlertCircle className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-bold">Confirm Overwrite</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Regenerating timing will overwrite {tasks.length} existing tasks. This action cannot be undone. Continue?
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={() => {
                setShowTimingConfirm(false);
                setPendingComponentTemplate(null);
              }}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleConfirmTimingOverwrite}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    active: '进行中',
    completed: '已完成',
    archived: '已归档',
  };
  return labels[status] || status;
}
